import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { LoadingButton } from '@mui/lab'
import { LinearProgress, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useTransmissionStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getVerticalTransmissionGraph } from '@queries'
import { recalculateVerticalTargets } from '@mutations'
import { buildErrorMessage } from 'src/constants'

interface Props {
  elementGuids: string[]
}

const MultiEditVerticalConnectionsReset = ({ elementGuids }: Props): ReactElement => {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const invalidateResultsQueries = useResultsInvalidation()
  const setVerticalTransmissionGraph = useTransmissionStore(
    state => state.setVerticalTransmissionGraph,
  )

  const [isRecalculatingConnections, setIsRecalculatingConnections] = useState(false)
  const [recalculatedConnectionsCount, setRecalculatedConnectionsCount] = useState(0)

  const progressInPercent = useMemo(() => {
    if (elementGuids.length === 0) return 0
    return (recalculatedConnectionsCount / elementGuids.length) * 100
  }, [elementGuids.length, recalculatedConnectionsCount])

  const handleRecalculateConnections = async () => {
    if (!projectId || elementGuids.length === 0) return

    setIsRecalculatingConnections(true)
    setRecalculatedConnectionsCount(0)

    try {
      for (let i = 0; i < elementGuids.length; i += 1) {
        const elementGuid = elementGuids[i]
        const updatedGraph = await recalculateVerticalTargets.request(projectId, elementGuid)
        setVerticalTransmissionGraph(updatedGraph)
        setRecalculatedConnectionsCount(i + 1)
      }

      await queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId))
      invalidateResultsQueries(projectId)
      enqueueSnackbar('Verbindungen erfolgreich neu berechnet', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Neuberechnen der Verbindungen'),
        { variant: 'error' },
      )
    } finally {
      setIsRecalculatingConnections(false)
    }
  }

  return (
    <Stack direction="column" spacing={1}>
      <LoadingButton
        onClick={handleRecalculateConnections}
        color="primary"
        variant="outlined"
        size="small"
        loading={isRecalculatingConnections}
        data-cy="recalculate-rip-connections-btn"
      >
        Verbindungen neu berechnen
      </LoadingButton>
      {isRecalculatingConnections && (
        <Stack spacing={0.5}>
          <LinearProgress variant="determinate" value={progressInPercent} />
          <Typography variant="caption" color="text.secondary">
            {`${recalculatedConnectionsCount} / ${elementGuids.length}`}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default MultiEditVerticalConnectionsReset
